import React, { useState, useEffect, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import {
	FileText,
	Search,
	ChevronRight,
	Eye,
	X as XIcon,
	Download,
	MessageSquare,
	FileDown,
} from "lucide-react";

import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogClose,
	DialogFooter,
} from "../../components/ui/dialog";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../../components/ui/tooltip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import EmptyStateCard from "../../components/EmptyStateCard";
import ModalImageCors from "../../components/ModalImageCors";
import { getPublicFileUrl } from "../../config";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useNavigate } from "react-router-dom";

// ================================================================
// Helpers UI
// ================================================================
function perfilBadge(profile) {
	const p = String(profile || "").toLowerCase();
	if (p === "admin") return { label: "Admin", cls: "bg-primary text-primary-foreground" };
	if (p === "supervisor") return { label: "Supervisor", cls: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200" };
	return { label: "Atendente", cls: "bg-muted text-muted-foreground" };
}

function statusBadgeTicket(status) {
	switch (String(status || "").toLowerCase()) {
		case "open":
		case "pending":
			return { label: "Aberto", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" };
		case "closed":
			return { label: "Fechado", cls: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200" };
		case "deleted":
			return { label: "Excluído", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200" };
		default:
			return { label: String(status || "—"), cls: "bg-muted text-muted-foreground" };
	}
}

function entidadeBadge(entity) {
	switch (String(entity || "").toLowerCase()) {
		case "contact":
			return { label: "Contato", cls: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200" };
		case "ticket":
			return { label: "Atendimento", cls: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200" };
		case "subscription":
			return { label: "Assinatura", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" };
		case "company":
			return { label: "Empresa", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200" };
		// Novas entities 2026-10-07 (pedido VERBATIM usuario: login + role assign + user)
		case "session":
			return { label: "Sessão", cls: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200" };
		case "user":
			return { label: "Usuário", cls: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-200" };
		case "role":
			return { label: "Cargo", cls: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200" };
		case "plan":
			return { label: "Plano", cls: "bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-200" };
		case "invoice":
			return { label: "Fatura", cls: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200" };
		case "coupon":
			return { label: "Cupom", cls: "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-200" };
		case "limit":
			return { label: "Limite", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200" };
		case "module":
			return { label: "Módulo", cls: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200" };
		default:
			return { label: String(entity || "—"), cls: "bg-muted text-muted-foreground" };
	}
}

function acaoBadge(action) {
	switch (String(action || "").toLowerCase()) {
		case "delete":
			return { label: "Excluir", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200" };
		case "delete-bulk":
			return { label: "Excluir em lote", cls: "bg-red-200 text-red-800 dark:bg-red-900/60 dark:text-red-100" };
		case "update":
			return { label: "Editar", cls: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200" };
		case "create":
			return { label: "Criar", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200" };
		case "activate":
			return { label: "Ativar", cls: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-200" };
		case "deactivate":
			return { label: "Desativar", cls: "bg-stone-200 text-stone-800 dark:bg-stone-800 dark:text-stone-200" };
		case "pause":
			return { label: "Pausar", cls: "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200" };
		case "cancel":
			return { label: "Cancelar", cls: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200" };
		// Novas actions 2026-10-07 (pedido VERBATIM usuario: login + assign)
		case "login_success":
			return { label: "Login OK", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200" };
		case "login_failure":
			return { label: "Login falhou", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200" };
		case "logout":
			return { label: "Logout", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200" };
		case "assign":
			return { label: "Atribuir cargo", cls: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200" };
		case "access_update":
			return { label: "Atualizar acesso", cls: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200" };
		default:
			return { label: String(action || "—"), cls: "bg-muted text-muted-foreground" };
	}
}

function formatarDataHora(dateStr) {
	if (!dateStr) return "—";
	try {
		const d = new Date(dateStr);
		if (Number.isNaN(d.getTime())) return String(dateStr);
		return d.toLocaleString("pt-BR", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
		});
	} catch {
		return String(dateStr);
	}
}

function formatarData(dateStr) {
	if (!dateStr) return "—";
	try {
		const d = new Date(dateStr);
		if (Number.isNaN(d.getTime())) return String(dateStr);
		return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
	} catch {
		return String(dateStr);
	}
}

function iniciaisNome(name) {
	if (!name) return "??";
	const partes = String(name).trim().split(/\s+/);
	if (partes.length === 0) return "??";
	if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
	return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

function jsonPretty(obj) {
	if (obj == null) return null;
	try {
		return JSON.stringify(obj, null, 2);
	} catch {
		return String(obj);
	}
}

function rotuloMidia(tipo) {
	switch (String(tipo || "").toLowerCase()) {
		case "image": return "Imagem";
		case "video": return "Vídeo";
		case "audio":
		case "ptt": return "Áudio";
		case "sticker": return "Figurinha";
		case "document":
		case "application": return "Documento";
		case "location": return "Localização";
		case "vcard": return "Contato";
		default: return tipo ? String(tipo) : null;
	}
}

// ================================================================
// Página
// ================================================================
const AuditLogs = () => {
	const { user } = useContext(AuthContext);
	const navigate = useNavigate();

	// Bloqueio frontend (defesa dupla — backend também bloqueia).
	const perfil = String(user?.profile || "").toLowerCase();
	if (perfil !== "admin") {
		// Se alguém colar a URL direto, redireciona e avisa.
		toast.error("Apenas administrador pode acessar os logs de auditoria.");
		setTimeout(() => navigate("/tickets", { replace: true }), 50);
		return null;
	}

	const [loading, setLoading] = useState(false);
	const [count, setCount] = useState(0);
	const [rows, setRows] = useState([]);
	const [hasMore, setHasMore] = useState(false);
	const [pageNumber, setPageNumber] = useState(1);
	const PAGE_SIZE = 30;

	// Padrão: últimos 30 dias até hoje (traz histórico visível logo no primeiro
	// carregamento, sem depender do usuário preencher datas). O "Limpar filtros"
	// volta para este padrão (não para datas vazias) para manter a consistência.
	const hoje = new Date();
	const trintaDiasAtras = new Date();
	trintaDiasAtras.setDate(hoje.getDate() - 30);
	const fmt = (d) => d.toISOString().slice(0, 10);
	const DATA_INICIAL_PADRAO = fmt(trintaDiasAtras);
	const DATA_FINAL_PADRAO = fmt(hoje);

	// Filtros
	const [searchParam, setSearchParam] = useState("");
	const [entity, setEntity] = useState("all");
	const [action, setAction] = useState("all");
	const [dateStart, setDateStart] = useState(DATA_INICIAL_PADRAO);
	const [dateEnd, setDateEnd] = useState(DATA_FINAL_PADRAO);

	// Filtros ativos (aplicados no botão Filtrar ou Enter)
	const [appliedFilters, setAppliedFilters] = useState({
		searchParam: "",
		entity: "all",
		action: "all",
		dateStart: DATA_INICIAL_PADRAO,
		dateEnd: DATA_FINAL_PADRAO,
	});

	// Modal detalhe
	const [selected, setSelected] = useState(null);
	const [detailOpen, setDetailOpen] = useState(false);
	const abrirDetalhe = (log) => {
		setSelected(log);
		setDetailOpen(true);
	};

	// Modal Ticket Apagado (histórico completo)
	const [deletedTicket, setDeletedTicket] = useState(null);
	const [deletedTicketLoading, setDeletedTicketLoading] = useState(false);
	const [deletedTicketOpen, setDeletedTicketOpen] = useState(false);
	const [deletedTicketPdfLoading, setDeletedTicketPdfLoading] = useState(false);

	// Cache de nomes de contato para exibir ao lado do ID do ticket (entity=ticket)
	const [nomeContatoMap, setNomeContatoMap] = useState(() => new Map());
	const carregandoRef = React.useRef(new Set());

	const carregarNomeContatoPorContactId = useCallback(async (contactId) => {
		const id = Number(contactId);
		if (!id || !Number.isFinite(id) || id <= 0) return null;
		if (nomeContatoMap.has(id)) return nomeContatoMap.get(id) || null;
		if (carregandoRef.current.has(id)) return null;
		carregandoRef.current.add(id);
		try {
			const { data } = await api.get(`/contacts/${id}`);
			const nome = data?.name || null;
			setNomeContatoMap((anterior) => {
				const prox = new Map(anterior);
				prox.set(id, nome);
				return prox;
			});
			return nome;
		} catch {
			setNomeContatoMap((anterior) => {
				const prox = new Map(anterior);
				prox.set(id, null);
				return prox;
			});
			return null;
		} finally {
			carregandoRef.current.delete(id);
		}
	}, [nomeContatoMap]);

	const getNomeContatoDoLog = useCallback((log) => {
		if (!log) return null;
		if (String(log.entity || "").toLowerCase() !== "ticket") return null;
		const contactId = Number(log?.before?.contactId || log?.after?.contactId || null);
		if (!contactId) return null;
		return nomeContatoMap.get(contactId) ?? null;
	}, [nomeContatoMap]);

	// Carrega em lote os contatos dos tickets exibidos na lista
	useEffect(() => {
		if (!rows || rows.length === 0) return;
		const ids = new Set();
		rows.forEach((log) => {
			if (String(log.entity || "").toLowerCase() !== "ticket") return;
			const cid = Number(log?.before?.contactId || log?.after?.contactId || null);
			if (cid && !nomeContatoMap.has(cid) && !carregandoRef.current.has(cid)) {
				ids.add(cid);
			}
		});
		ids.forEach((id) => { carregarNomeContatoPorContactId(id).catch(() => null); });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rows]);

	const abrirTicketApagado = useCallback(async (log) => {
		if (!log) return;
		if (String(log.entity || "").toLowerCase() !== "ticket") return;
		if (String(log.action || "").toLowerCase() !== "delete") return;
		setDeletedTicketLoading(true);
		try {
			const { data } = await api.get(`/audit-logs/deleted-ticket/${log.entityId}`);
			setDeletedTicket(data || null);
			setDeletedTicketOpen(true);
			if (!data || !data?.messages?.length) {
				toast.info("Este atendimento não contém mensagens no histórico.");
			}
		} catch (err) {
			if (err?.response?.status === 404) {
				toast.warning("Ticket não encontrado no banco (possivelmente excluído fisicamente).");
				setDeletedTicket(null);
				setDeletedTicketOpen(true);
			} else {
				toastError(err);
			}
		} finally {
			setDeletedTicketLoading(false);
		}
	}, []);

	const [pdfLoading, setPdfLoading] = useState(false);
	const exportarPDF = useCallback(async () => {
		setPdfLoading(true);
		try {
			const params = new URLSearchParams();
			if (appliedFilters.searchParam) params.set("searchParam", appliedFilters.searchParam);
			if (appliedFilters.entity && appliedFilters.entity !== "all") params.set("entity", appliedFilters.entity);
			if (appliedFilters.action && appliedFilters.action !== "all") params.set("action", appliedFilters.action);
			if (appliedFilters.dateStart) params.set("dateStart", appliedFilters.dateStart);
			if (appliedFilters.dateEnd) params.set("dateEnd", appliedFilters.dateEnd);

			const qs = params.toString();
			const url = `/audit-logs/pdf${qs ? `?${qs}` : ""}`;
			const { data, headers } = await api.get(url, { responseType: "blob" });

			const disposition = headers?.["content-disposition"] || headers?.["Content-Disposition"] || "";
			let nome = `logs-auditoria-${new Date().toISOString().slice(0, 10)}.pdf`;
			const match = /filename\*?=(?:UTF-8'')?([^;\r\n]+)/i.exec(disposition);
			if (match?.[1]) {
				try { nome = decodeURIComponent(match[1].replace(/^["']|["']$/g, "")); } catch { nome = match[1]; }
			}

			const blob = new Blob([data], { type: "application/pdf" });
			const href = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = href;
			a.download = nome;
			document.body.appendChild(a);
			a.click();
			a.remove();
			setTimeout(() => URL.revokeObjectURL(href), 2000);
			toast.success(`PDF “${nome}” gerado com sucesso.`);
		} catch (err) {
			toastError(err);
		} finally {
			setPdfLoading(false);
		}
	}, [appliedFilters]);

	const exportarChatApagadoPdf = useCallback(async (id) => {
		setDeletedTicketPdfLoading(true);
		try {
			const url = `/audit-logs/deleted-ticket/${id}/pdf`;
			const { data, headers } = await api.get(url, { responseType: "blob" });

			const disposition = headers?.["content-disposition"] || headers?.["Content-Disposition"] || "";
			let nome = `atendimento-apagado-${id}.pdf`;
			const match = /filename\*?=(?:UTF-8'')?([^;\r\n]+)/i.exec(disposition);
			if (match?.[1]) {
				try { nome = decodeURIComponent(match[1].replace(/^["']|["']$/g, "")); } catch { nome = match[1]; }
			}

			const blob = new Blob([data], { type: "application/pdf" });
			const href = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = href;
			a.download = nome;
			document.body.appendChild(a);
			a.click();
			a.remove();
			setTimeout(() => URL.revokeObjectURL(href), 2000);
			toast.success(`PDF “${nome}” gerado com sucesso.`);
		} catch (err) {
			toastError(err);
		} finally {
			setDeletedTicketPdfLoading(false);
		}
	}, []);

	const carregar = useCallback(async (reset = false, filtros = appliedFilters) => {
		setLoading(true);
		try {
			const params = new URLSearchParams();
			params.set("pageNumber", String(reset ? 1 : pageNumber));
			params.set("pageSize", String(PAGE_SIZE));
			if (filtros.searchParam) params.set("searchParam", filtros.searchParam);
			if (filtros.entity && filtros.entity !== "all") params.set("entity", filtros.entity);
			if (filtros.action && filtros.action !== "all") params.set("action", filtros.action);
			if (filtros.dateStart) params.set("dateStart", filtros.dateStart);
			if (filtros.dateEnd) params.set("dateEnd", filtros.dateEnd);

			const { data } = await api.get(`/audit-logs?${params.toString()}`);
			const novos = Array.isArray(data?.rows) ? data.rows : [];
			setCount(Number(data?.count || 0));
			setHasMore(Boolean(data?.hasMore) || novos.length >= PAGE_SIZE && count > (reset ? 0 : rows.length) + novos.length);
			if (reset) {
				setRows(novos);
				setPageNumber(2);
			} else {
				setRows((prev) => [...prev, ...novos]);
				setPageNumber((p) => p + 1);
			}
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageNumber, appliedFilters]);

	// Primeira carga
	useEffect(() => {
		carregar(true, appliedFilters);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appliedFilters]);

	const aplicarFiltros = (e) => {
		if (e?.preventDefault) e.preventDefault();
		const next = { searchParam: searchParam.trim(), entity, action, dateStart, dateEnd };
		setAppliedFilters(next);
	};

	const limparFiltros = () => {
		setSearchParam("");
		setEntity("all");
		setAction("all");
		setDateStart(DATA_INICIAL_PADRAO);
		setDateEnd(DATA_FINAL_PADRAO);
		setAppliedFilters({ searchParam: "", entity: "all", action: "all", dateStart: DATA_INICIAL_PADRAO, dateEnd: DATA_FINAL_PADRAO });
	};

	const carregarMais = () => !loading && hasMore && carregar(false, appliedFilters);
	const isDeleteTicket = (log) =>
		String(log?.entity || "").toLowerCase() === "ticket" &&
		String(log?.action || "").toLowerCase() === "delete";

	return (
		<TooltipProvider>
			<MainContainer>
				<Dialog open={detailOpen} onOpenChange={(o) => !o && setDetailOpen(false)}>
					<DialogContent className="max-w-4xl">
						{selected && (
							<>
								<DialogHeader>
									<DialogTitle className="flex items-center gap-2 flex-wrap">
										<Badge className={entidadeBadge(selected.entity).cls}>{entidadeBadge(selected.entity).label}</Badge>
										<Badge className={acaoBadge(selected.action).cls}>{acaoBadge(selected.action).label}</Badge>
										<span className="text-sm font-normal text-muted-foreground">
											ID {selected.entityId == null ? "N/A" : `#${selected.entityId}`}
											{selected && getNomeContatoDoLog(selected) && (
												<> · <span className="font-medium text-foreground/90">{getNomeContatoDoLog(selected)}</span></>
											)}
										</span>
									</DialogTitle>
									<DialogDescription className="text-left">
										{formatarDataHora(selected.createdAt)} · por {selected.user?.name || "Sistema"} ({selected.user?.email || "—"})
									</DialogDescription>
								</DialogHeader>

								<div className="grid gap-3 text-sm">
									<div className="grid grid-cols-1 gap-2 rounded-xl border bg-muted/30 p-4 sm:grid-cols-2 md:grid-cols-3">
										<div>
											<span className="text-xs uppercase tracking-wider text-muted-foreground">Usuário</span>
											<p className="font-medium">{selected.user?.name || "Sistema"}</p>
											<p className="text-xs text-muted-foreground">{selected.user?.email || "—"}</p>
										</div>
										<div>
											<span className="text-xs uppercase tracking-wider text-muted-foreground">Horário</span>
											<p className="font-medium">{formatarDataHora(selected.createdAt)}</p>
										</div>
										<div>
											<span className="text-xs uppercase tracking-wider text-muted-foreground">Perfil</span>
											<p><Badge className={perfilBadge(selected.user?.profile).cls}>{perfilBadge(selected.user?.profile).label}</Badge></p>
										</div>
										<div>
											{/* Campo NOVA compliance 2026-10-07: pedido VERBATIM usuário */}
											<span className="text-xs uppercase tracking-wider text-muted-foreground">IP de origem</span>
											<p className="font-mono break-all text-sm">{selected.ipAddress || "—"}</p>
										</div>
										<div className="sm:col-span-2">
											{/* User-Agent é longo — quebra de linha, col-span-2 no desktop */}
											<span className="text-xs uppercase tracking-wider text-muted-foreground">Dispositivo / Navegador (User-Agent)</span>
											<p className="break-all font-mono text-xs leading-relaxed text-muted-foreground/90">
												{selected.userAgent || "—"}
											</p>
										</div>
										<div className="sm:col-span-3">
											<span className="text-xs uppercase tracking-wider text-muted-foreground">Motivo</span>
											<p className="font-medium break-all">{selected.reason || "—"}</p>
										</div>
									</div>

									{isDeleteTicket(selected) && (
										<div className="rounded-xl border border-red-200 bg-red-50/60 p-4 dark:border-red-900/40 dark:bg-red-950/30">
											<div className="flex flex-wrap items-center justify-between gap-2">
												<div className="flex items-center gap-2">
													<MessageSquare className="h-4 w-4 text-red-600 dark:text-red-300" />
													<h4 className="text-sm font-semibold text-red-800 dark:text-red-200">
														Ticket apagado #{selected.entityId}
														{getNomeContatoDoLog(selected) && (
															<> · <span className="font-medium">{getNomeContatoDoLog(selected)}</span></>
														)}{" — histórico completo disponível"}
													</h4>
												</div>
												<Button
													size="sm"
													variant="default"
													onClick={() => {
														setDetailOpen(false);
														abrirTicketApagado(selected);
													}}
												>
													<MessageSquare className="mr-1 h-3.5 w-3.5" />
													Ver Ticket Apagado
												</Button>
											</div>
											<p className="mt-2 text-xs text-red-700/80 dark:text-red-300/80">
												Abre os dados do atendimento, quem deletou, por quê, e TODAS as mensagens da conversa em ordem.
											</p>
										</div>
									)}

									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<div className="mb-1.5 flex items-center justify-between">
												<h4 className="text-sm font-semibold">Snapshot ANTES</h4>
												{!selected.before && <Badge variant="outline" className="text-muted-foreground">Sem valor antes</Badge>}
											</div>
											<div className="h-80 overflow-auto rounded-xl border bg-background p-3">
												<pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">
													{jsonPretty(selected.before) || "{}"}
												</pre>
											</div>
										</div>
										<div>
											<div className="mb-1.5 flex items-center justify-between">
												<h4 className="text-sm font-semibold">Snapshot DEPOIS</h4>
												{!selected.after && <Badge variant="outline" className="text-muted-foreground">Sem valor depois</Badge>}
											</div>
											<div className="h-80 overflow-auto rounded-xl border bg-background p-3">
												<pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">
													{jsonPretty(selected.after) || "{}"}
												</pre>
											</div>
										</div>
									</div>
								</div>

								<DialogFooter>
									<DialogClose asChild>
										<Button variant="outline"><XIcon className="mr-2 h-4 w-4"/>Fechar</Button>
									</DialogClose>
								</DialogFooter>
							</>
						)}
					</DialogContent>
				</Dialog>

				{/* Modal: Ticket Apagado Histórico Completo */}
				<Dialog
					open={deletedTicketOpen}
					onOpenChange={(o) => !o && setDeletedTicketOpen(false)}
				>
					<DialogContent className="max-w-4xl">
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2 flex-wrap">
								<Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200">
									Atendimento Apagado
								</Badge>
								<span className="text-sm font-normal text-muted-foreground">
									ID #{deletedTicket?.ticket?.id ?? "??"}
									{deletedTicket?.ticket?.contato && (
										<> · <span className="font-medium text-foreground/90">{deletedTicket.ticket.contato}</span></>
									)}
								</span>
								{deletedTicket?.ticket?.id && (
									<Button
										variant="destructive"
										size="sm"
										onClick={() => exportarChatApagadoPdf(deletedTicket.ticket.id)}
										disabled={deletedTicketPdfLoading || deletedTicketLoading}
										className="ml-auto"
									>
										{deletedTicketPdfLoading ? (
											<>
												<span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent mr-2"/>
												Gerando PDF…
											</>
										) : (
											<>
												<Download className="h-4 w-4 mr-1"/>
												Exportar PDF do Atendimento
											</>
										)}
									</Button>
								)}
							</DialogTitle>
							<DialogDescription className="text-left">
								Histórico íntegro do atendimento (conversa preservada — exclusão lógica apenas).
							</DialogDescription>
						</DialogHeader>

						{deletedTicketLoading && (
							<div className="py-10 text-center text-sm text-muted-foreground">
								Carregando histórico do atendimento…
							</div>
						)}

						{!deletedTicketLoading && !deletedTicket && (
							<div className="rounded-xl border border-amber-200 bg-amber-50/60 p-5 dark:border-amber-800/50 dark:bg-amber-950/30">
								<div className="flex items-center gap-2">
									<Badge className="bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100">
										Aviso
									</Badge>
									<h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
										Ticket não encontrado no banco (possivelmente excluído fisicamente).
									</h4>
								</div>
								<p className="mt-2 text-xs text-amber-800/80 dark:text-amber-200/80">
									Os snapshots ANTES/DEPOIS continuam disponíveis no modal de detalhe do log (feche este para consultar).
								</p>
							</div>
						)}

						{!deletedTicketLoading && deletedTicket && (
							<>
								{/* Resumo */}
								<div className="grid grid-cols-1 gap-3 rounded-xl border bg-muted/30 p-4 sm:grid-cols-2 lg:grid-cols-3">
									<div>
										<span className="text-xs uppercase tracking-wider text-muted-foreground">Contato</span>
										<div className="mt-1 flex items-center gap-2">
											<Avatar className="h-8 w-8">
												<AvatarFallback className="text-xs font-semibold">{iniciaisNome(deletedTicket.ticket?.contato)}</AvatarFallback>
											</Avatar>
											<div className="min-w-0">
												<p className="truncate text-sm font-medium">{deletedTicket.ticket?.contato || "—"}</p>
												<p className="truncate text-xs text-muted-foreground">{deletedTicket.ticket?.numero || "—"}</p>
											</div>
										</div>
									</div>
									<div>
										<span className="text-xs uppercase tracking-wider text-muted-foreground">Atendente / Fila</span>
										<p className="mt-1 text-sm font-medium">{deletedTicket.ticket?.atendente || "Sem atendente"}</p>
										<p className="text-xs text-muted-foreground">
											Fila: {deletedTicket.ticket?.fila || "Sem fila"} · Conexão: {deletedTicket.ticket?.conexao || "—"}
										</p>
									</div>
									<div>
										<span className="text-xs uppercase tracking-wider text-muted-foreground">Status</span>
										<p className="mt-1">
											<Badge className={statusBadgeTicket(deletedTicket.ticket?.status).cls}>
												{statusBadgeTicket(deletedTicket.ticket?.status).label}
											</Badge>
											{deletedTicket.ticket?.isGroup ? (
												<Badge variant="outline" className="ml-2">Grupo</Badge>
											) : null}
										</p>
										<p className="text-xs text-muted-foreground">
											Criado: {formatarDataHora(deletedTicket.ticket?.criadoEm)}
										</p>
									</div>
									<div>
										<span className="text-xs uppercase tracking-wider text-muted-foreground">Última atualização</span>
										<p className="mt-1 text-sm font-medium">{formatarDataHora(deletedTicket.ticket?.atualizadoEm)}</p>
									</div>
									<div>
										<span className="text-xs uppercase tracking-wider text-muted-foreground">Deletado em / por</span>
										<p className="mt-1 text-sm font-medium">{formatarDataHora(deletedTicket.ticket?.deletadoEm)}</p>
										<p className="text-xs text-muted-foreground">
											{(deletedTicket.ticket?.deletadoPorNome && deletedTicket.ticket?.deletadoPorEmail)
												? `${deletedTicket.ticket.deletadoPorNome} (${deletedTicket.ticket.deletadoPorEmail})`
												: deletedTicket.ticket?.deletadoPorNome || deletedTicket.ticket?.deletadoPorEmail || "—"}
										</p>
									</div>
									<div>
										<span className="text-xs uppercase tracking-wider text-muted-foreground">Motivo da exclusão</span>
										<p className="mt-1 break-words text-sm font-medium">
											{deletedTicket.ticket?.motivoDelecao || "—"}
										</p>
									</div>
									<div className="sm:col-span-2 lg:col-span-3">
										<div className="grid grid-cols-3 gap-3 rounded-lg border bg-background p-3 text-center">
											<div>
												<p className="text-xs uppercase tracking-wider text-muted-foreground">Total msgs</p>
												<p className="text-lg font-semibold">{deletedTicket.totais?.mensagens ?? 0}</p>
											</div>
											<div>
												<p className="text-xs uppercase tracking-wider text-muted-foreground">Áudios</p>
												<p className="text-lg font-semibold">{deletedTicket.totais?.audios ?? 0}</p>
											</div>
											<div>
												<p className="text-xs uppercase tracking-wider text-muted-foreground">Áudios transcritos</p>
												<p className="text-lg font-semibold">{deletedTicket.totais?.audiosTranscritos ?? 0}</p>
											</div>
										</div>
									</div>
								</div>

								{/* Histórico de mensagens */}
								<div className="mt-3 max-h-[50vh] overflow-auto rounded-xl border bg-muted/30 p-3 sm:p-4">
									<h4 className="mb-2 text-sm font-semibold text-muted-foreground">
										Histórico completo da conversa ({deletedTicket.messages?.length || 0} mensagens)
									</h4>
									{(!deletedTicket.messages?.length) && (
										<div className="p-4 text-center text-sm text-muted-foreground">
											Nenhuma mensagem registrada neste atendimento.
										</div>
									)}
									<ul className="flex flex-col gap-2">
										{deletedTicket.messages?.map?.((m) => {
											const midia = rotuloMidia(m.mediaType);
											const bolhaInterna = Boolean(m.isInternal);
											const daEquipe = Boolean(m.fromMe) || bolhaInterna;
											const clsLado = daEquipe ? "ml-auto" : "mr-auto";
											const clsCor = bolhaInterna
												? "bg-amber-100 text-amber-950 dark:bg-amber-900/50 dark:text-amber-50 border border-amber-300/70 dark:border-amber-700/60"
												: daEquipe
													? "bg-emerald-100 text-emerald-950 dark:bg-emerald-900/60 dark:text-emerald-50"
													: "bg-white border border-slate-200 text-slate-900 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100";
											return (
												<li key={m.id} className={`max-w-[85%] ${clsLado}`}>
													<div className={`rounded-2xl px-3 py-2 text-sm ${clsCor}`}>
														<div className="mb-1 flex items-center gap-2 text-[11px] font-medium opacity-80">
															<span className="truncate">{m.autor || "—"}</span>
															<span>·</span>
															<span>{formatarDataHora(m.createdAt).split(" ")[1]}</span>
															{midia && (
																<>
																	<span>·</span>
																	<Badge variant="outline" className="h-4 px-1 text-[10px]">{midia}</Badge>
																</>
															)}
														</div>
														{(() => {
															const temCorpo = Boolean(m.body?.trim?.());
															const tipoBase = String(m.mediaType || "")
																.split("/")[0]
																.toLowerCase();
															const ehMidiaImagem =
																Boolean(m.mediaUrl) &&
																Boolean(m.mediaType) &&
																(tipoBase === "image" || tipoBase === "sticker");
															const urlMidia = ehMidiaImagem ? getPublicFileUrl(m.mediaUrl) : "";
															return (
																<>
																	{ehMidiaImagem && urlMidia ? (
																		<div className="mb-1 max-w-[200px]">
																			<ModalImageCors imageUrl={urlMidia} />
																		</div>
																	) : null}
																	{(temCorpo || (!ehMidiaImagem && midia)) ? (
																		<p className="whitespace-pre-wrap break-words leading-relaxed">
																			{temCorpo ? m.body?.trim?.() : midia ? `[${midia}]` : ""}
																		</p>
																	) : null}
																</>
															);
														})()}
														{m.quotedBody ? (
															<div className="mt-1 rounded-lg border border-black/5 bg-white/50 px-2 py-1 text-xs italic opacity-80 dark:border-white/10 dark:bg-black/20">
																Em resposta a: {m.quotedBody}
															</div>
														) : null}
														{m.transcription ? (
															<div className="mt-1 rounded-lg border border-black/5 bg-white/60 px-2 py-1 text-[11px] dark:border-white/10 dark:bg-black/30">
																<strong>Transcrição:</strong> {m.transcription}
															</div>
														) : null}
													</div>
												</li>
											);
										})}
									</ul>
								</div>
							</>
						)}

						<DialogFooter>
							<DialogClose asChild>
								<Button variant="outline"><XIcon className="mr-2 h-4 w-4"/>Fechar histórico</Button>
							</DialogClose>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				<MainHeader>
					<div className="min-w-0 flex-1">
						<Title><FileText className="mr-2 inline h-5 w-5 align-middle" />Logs de Auditoria</Title>
						<p className="mt-1 text-xs text-muted-foreground sm:text-sm">
							Todas as ações administrativas importantes executadas nesta empresa ficam registradas aqui para auditoria.
							Nenhum registro pode ser alterado ou apagado.
						</p>
						{count > 0 && (
							<p className="mt-1 text-xs text-muted-foreground">
								<span className="font-medium text-foreground">{count}</span> registro(s) encontrado(s).
							</p>
						)}
					</div>
					<div className="mt-3 flex shrink-0 items-center gap-2 sm:mt-0">
						<Button
							variant="outline"
							onClick={exportarPDF}
							disabled={pdfLoading || loading && rows.length === 0}
						>
							{pdfLoading ? (
								<>
									<span className="mr-1 h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
									Gerando PDF…
								</>
							) : (
								<>
									<Download className="mr-1 h-3.5 w-3.5" />
									Exportar PDF
								</>
							)}
						</Button>
					</div>
				</MainHeader>

				<div className="flex-1 overflow-auto px-4 pb-6 sm:px-6">
					{/* Barra de filtros */}
					<form onSubmit={aplicarFiltros} className="mb-4 grid grid-cols-1 gap-2 rounded-xl border bg-card p-3 sm:grid-cols-12 sm:items-end">
						<div className="sm:col-span-4">
							<label className="mb-1 block text-xs font-medium text-muted-foreground">Buscar</label>
							<div className="relative">
								<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									placeholder="Por motivo, nome, entidade, ação..."
									value={searchParam}
									onChange={(e) => setSearchParam(e.target.value)}
									className="pl-9"
								/>
							</div>
						</div>
						<div className="sm:col-span-2">
							<label className="mb-1 block text-xs font-medium text-muted-foreground">Entidade</label>
							<Select value={entity} onValueChange={setEntity}>
								<SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
								<SelectContent>
									<SelectItem value="all">Todos</SelectItem>
									<SelectItem value="ticket">Atendimentos</SelectItem>
									<SelectItem value="contact">Contatos</SelectItem>
									<SelectItem value="subscription">Assinaturas</SelectItem>
									<SelectItem value="company">Empresas</SelectItem>
									{/* Novas entities 2026-10-07 (pedido VERBATIM usuário: sessão, cargo, usuário) */}
									<SelectItem value="session">Sessões (login/logout)</SelectItem>
									<SelectItem value="user">Usuários (ativar/desativar/acesso)</SelectItem>
									<SelectItem value="role">Cargos (role assign)</SelectItem>
									<SelectItem value="plan">Planos</SelectItem>
									<SelectItem value="invoice">Faturas</SelectItem>
									<SelectItem value="coupon">Cupons</SelectItem>
									<SelectItem value="limit">Limites</SelectItem>
									<SelectItem value="module">Módulos</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="sm:col-span-2">
							<label className="mb-1 block text-xs font-medium text-muted-foreground">Ação</label>
							<Select value={action} onValueChange={setAction}>
								<SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
								<SelectContent>
									<SelectItem value="all">Todas</SelectItem>
									<SelectItem value="create">Criar</SelectItem>
									<SelectItem value="update">Editar</SelectItem>
									<SelectItem value="delete">Excluir</SelectItem>
									<SelectItem value="delete-bulk">Excluir em lote</SelectItem>
									<SelectItem value="activate">Ativar</SelectItem>
									<SelectItem value="deactivate">Desativar</SelectItem>
									<SelectItem value="pause">Pausar</SelectItem>
									<SelectItem value="cancel">Cancelar</SelectItem>
									{/* Novas actions 2026-10-07 (pedido VERBATIM usuário: login + assign + acesso) */}
									<SelectItem value="login_success">Login OK</SelectItem>
									<SelectItem value="login_failure">Login falhou</SelectItem>
									<SelectItem value="logout">Logout</SelectItem>
									<SelectItem value="assign">Atribuir cargo</SelectItem>
									<SelectItem value="access_update">Atualizar acesso</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="sm:col-span-2">
							<label className="mb-1 block text-xs font-medium text-muted-foreground">De</label>
							<Input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
						</div>
						<div className="sm:col-span-2">
							<label className="mb-1 block text-xs font-medium text-muted-foreground">Até</label>
							<Input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
						</div>
						<div className="mt-2 flex flex-wrap items-center gap-2 sm:col-span-12 sm:mt-0 sm:justify-end">
							<Button type="button" variant="ghost" onClick={limparFiltros}>Limpar filtros</Button>
							<Button type="submit">
								<ChevronRight className="mr-1 h-4 w-4"/>Filtrar
							</Button>
						</div>
					</form>

					{/* Tabela (sm+) */}
					<div className="hidden rounded-xl border bg-card sm:block">
						<Table>
							<TableHeader className="sticky top-0 z-10 bg-card">
								<TableRow>
									<TableHead className="w-44">Data / Hora</TableHead>
									<TableHead>Executado por</TableHead>
									{/* Coluna NOVA 2026-10-07 (pedido VERBATIM do usuário: "mostrar o ip da
									  onde foi logado"). Mostramos o IP em qualquer ação (não só login),
									  pois ajuda auditoria geral (ex: qual IP alterou o cargo?). */}
									<TableHead className="w-40">IP</TableHead>
									<TableHead>Entidade</TableHead>
									<TableHead>Ação</TableHead>
									<TableHead className="w-24">ID Ent.</TableHead>
									<TableHead>Motivo</TableHead>
									<TableHead className="w-52 text-right">Ações</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{!loading && rows.length === 0 && (
									<TableRow>
										<TableCell colSpan={8} className="!p-0">
											<div className="p-6">
												<EmptyStateCard
													variant="tableCell"
													size="md"
													icon={FileText}
													title="Nenhum registro de auditoria"
													description="Quando alguém fizer login, alterar cargo de usuário, excluir atendimento/contato, o log aparecerá aqui automaticamente."
												/>
											</div>
										</TableCell>
									</TableRow>
								)}
								{rows.map((log) => {
									const entB = entidadeBadge(log.entity);
									const acB = acaoBadge(log.action);
									const pfB = perfilBadge(log.user?.profile);
									const del = isDeleteTicket(log);
									return (
										<TableRow key={log.id} className="align-middle">
											<TableCell className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
												{formatarDataHora(log.createdAt)}
											</TableCell>
											<TableCell>
												<div className="flex min-w-0 items-center gap-3">
													<Avatar className="h-8 w-8">
														<AvatarFallback className="text-xs font-semibold">
															{iniciaisNome(log.user?.name)}
														</AvatarFallback>
													</Avatar>
													<div className="min-w-0">
														<div className="flex items-center gap-2">
															<p className="truncate text-sm font-medium">{log.user?.name || "Sistema"}</p>
															<Badge variant="outline" className={pfB.cls + " border-0 px-1.5 py-0 text-[10px]"}>{pfB.label}</Badge>
														</div>
														<p className="truncate text-xs text-muted-foreground">{log.user?.email || "—"}</p>
													</div>
												</div>
											</TableCell>
											<TableCell>
												{log.ipAddress ? (
													<TooltipProvider delayDuration={100}>
														<Tooltip>
															<TooltipTrigger asChild>
																<code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-muted-foreground">
																	{String(log.ipAddress).length > 18 ? String(log.ipAddress).slice(0, 18) + "…" : log.ipAddress}
																</code>
															</TooltipTrigger>
															<TooltipContent side="top" className="font-mono text-xs">{log.ipAddress}</TooltipContent>
														</Tooltip>
													</TooltipProvider>
												) : (
													<span className="text-xs text-muted-foreground/60">—</span>
												)}
											</TableCell>
											<TableCell>
												<Badge className={entB.cls}>{entB.label}</Badge>
											</TableCell>
											<TableCell>
												<Badge className={acB.cls}>{acB.label}</Badge>
											</TableCell>
											<TableCell className="tabular-nums text-muted-foreground">
												<div>
													<div>{log.entityId == null ? "—" : `#${log.entityId}`}</div>
													{(String(log.entity || "").toLowerCase() === "ticket") && getNomeContatoDoLog(log) && (
														<div className="mt-0.5 truncate text-[11px] font-medium text-foreground/80">
															{getNomeContatoDoLog(log)}
														</div>
													)}
												</div>
											</TableCell>
											<TableCell>
												{log.reason ? (
													<TooltipProvider delayDuration={100}>
														<Tooltip>
															<TooltipTrigger asChild>
																<p className="max-w-md truncate text-sm text-muted-foreground">
																	{log.reason}
																</p>
															</TooltipTrigger>
															<TooltipContent side="top" className="max-w-md whitespace-pre-wrap">
																{log.reason}
															</TooltipContent>
														</Tooltip>
													</TooltipProvider>
												) : (
													<span className="text-sm text-muted-foreground/60">—</span>
												)}
											</TableCell>
											<TableCell className="text-right">
												<div className="inline-flex justify-end gap-1.5">
													{del && (
														<Button
															variant="default"
															size="sm"
															onClick={() => abrirTicketApagado(log)}
														>
															<MessageSquare className="mr-1 h-3.5 w-3.5"/>Ticket
														</Button>
													)}
													{del && (
														<Button
															variant="ghost"
															size="sm"
															onClick={() => exportarChatApagadoPdf(log.entityId)}
															title="Exportar PDF do atendimento apagado"
														>
															<FileDown className="mr-1 h-3.5 w-3.5"/>PDF
														</Button>
													)}
													<Button variant="outline" size="sm" onClick={() => abrirDetalhe(log)}>
														<Eye className="mr-1 h-3.5 w-3.5"/>Ver
													</Button>
												</div>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</div>

					{/* Cards (mobile) */}
					<ul className="space-y-2 sm:hidden">
						{!loading && rows.length === 0 && (
							<li className="p-6">
								<EmptyStateCard
									variant="tableCell"
									size="md"
									icon={FileText}
									title="Nenhum registro de auditoria"
									description="Quando alguém excluir um atendimento ou contato, o log aparecerá aqui automaticamente."
								/>
							</li>
						)}
						{rows.map((log) => {
							const entB = entidadeBadge(log.entity);
							const acB = acaoBadge(log.action);
							const del = isDeleteTicket(log);
							return (
								<li key={log.id} className="rounded-xl border bg-card p-3">
									<div className="mb-2 flex items-start justify-between gap-3">
										<div className="min-w-0">
											<div className="flex items-center gap-1.5">
												<Badge className={entB.cls}>{entB.label}</Badge>
												<Badge className={acB.cls}>{acB.label}</Badge>
												{del && (
													<Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200">Apagado</Badge>
												)}
											</div>
											<p className="mt-1 text-xs tabular-nums text-muted-foreground">
												{formatarDataHora(log.createdAt)}
											</p>
										</div>
										<div className="flex items-center gap-1.5">
											{del && (
												<Button size="sm" variant="default" onClick={() => abrirTicketApagado(log)} title="Ver histórico do ticket apagado">
													<MessageSquare className="h-3.5 w-3.5"/>
												</Button>
											)}
											{del && (
												<Button size="sm" variant="ghost" onClick={() => exportarChatApagadoPdf(log.entityId)} title="Exportar PDF do atendimento apagado">
													<FileDown className="h-3.5 w-3.5"/>
												</Button>
											)}
											<Button variant="outline" size="sm" onClick={() => abrirDetalhe(log)}>
												<Eye className="h-3.5 w-3.5"/>
											</Button>
										</div>
									</div>
									<div className="flex items-center gap-2">
										<Avatar className="h-7 w-7">
											<AvatarFallback className="text-[10px] font-semibold">{iniciaisNome(log.user?.name)}</AvatarFallback>
										</Avatar>
										<div className="min-w-0">
											<p className="truncate text-sm font-medium">{log.user?.name || "Sistema"}</p>
											<p className="truncate text-xs text-muted-foreground">{log.user?.email || "—"}</p>
										</div>
										<div className="ml-auto text-right">
											<div className="tabular-nums text-xs text-muted-foreground">
												{log.entityId == null ? "—" : `#${log.entityId}`}
											</div>
											{(String(log.entity || "").toLowerCase() === "ticket") && getNomeContatoDoLog(log) && (
												<div className="truncate text-[11px] font-medium text-foreground/80">
													{getNomeContatoDoLog(log)}
												</div>
											)}
										</div>
									</div>
									{/* NOVA coluna IP — versão mobile. Mesmo dado da coluna desktop (w-40)
									    com monoespaçado tabular-nums. Tooltip igual da coluna. */}
									{(log.ipAddress || log.userAgent) && (
										<TooltipProvider disableHoverableContent delayDuration={120}>
											<Tooltip>
												<TooltipTrigger asChild>
													<div className="mt-2 inline-flex max-w-full items-center gap-2 rounded-md border bg-muted/40 px-2 py-1">
														<span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
															IP
														</span>
														{log.ipAddress ? (
															<code className="truncate font-mono text-[11px] tabular-nums">
																{log.ipAddress.length > 24
																	? `${log.ipAddress.slice(0, 22)}…`
																	: log.ipAddress}
															</code>
														) : (
															<span className="text-[11px] text-muted-foreground">—</span>
														)}
														{log.userAgent && (
															<span className="shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground/70 border-l pl-2 border-border/80">
																UA
															</span>
														)}
													</div>
												</TooltipTrigger>
												<Tooltip side="top" className="max-w-sm">
													<div className="space-y-1">
														<p className="text-[11px] uppercase tracking-wider text-muted-foreground">IP de origem</p>
														<p className="font-mono text-[11px] tabular-nums break-all">{log.ipAddress || "—"}</p>
														{log.userAgent && (
															<>
																<p className="pt-1 text-[11px] uppercase tracking-wider text-muted-foreground">Dispositivo / Navegador</p>
																<p className="font-mono text-[11px] break-all leading-relaxed text-foreground/95">
																	{log.userAgent}
																</p>
															</>
														)}
													</div>
												</Tooltip>
											</Tooltip>
										</TooltipProvider>
									)}
									{log.reason && (
										<p className="mt-2 line-clamp-2 rounded-lg bg-muted p-2 text-xs text-muted-foreground">
											{log.reason}
										</p>
									)}
								</li>
							);
						})}
					</ul>

					{/* Carregar mais */}
					{rows.length > 0 && (
						<div className="mt-4 flex justify-center">
							<Button variant="outline" onClick={carregarMais} disabled={loading || !hasMore}>
								{loading
									? "Carregando..."
									: hasMore
										? "Carregar mais"
										: "Todos os registros carregados"}
							</Button>
						</div>
					)}
				</div>
			</MainContainer>
		</TooltipProvider>
	);
};

export default AuditLogs;
